function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
    );
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    this.dataset.section = this.closest('.shopify-section').id.replace('shopify-section-', '');
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`)
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}

customElements.define('variant-selects', VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

class BulkAdd extends HTMLElement {
  constructor() {
    super();
    this.queue = [];
    this.requestStarted = false;
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });
    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, 250);
  }

  sendRequest(queue) {
    this.requestStarted = true;
    const items = {};
    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));
    const quickBulkElement = this.closest('quick-order-list') || this.closest('quick-add-bulk');
    quickBulkElement.updateMultipleQty(items);
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.startQueue(index, inputValue);
    }
  }

  getSectionsUrl() {
    if (window.pageNumber) {
      return `${window.location.pathname}?page=${window.pageNumber}`;
    } else {
      return `${window.location.pathname}`;
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}


// custom js Ap-001

// js faq block
class CustomFaqRichtextSection extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.initFaq();
    }

    initFaq() {
      this.querySelectorAll('.faq-heading').forEach((item) => {
        item.addEventListener('click', () => this.toggleFaq(item));
      });
    }

    toggleFaq(item) {
      let parent = item.closest('.custom-faq-blocks');
      let content = parent.querySelector('.faq-content-custom');

      if (!content) return;

      if (parent.classList.contains('active')) {
        content.style.setProperty('--faq-height', content.scrollHeight + 'px');

        content.classList.add('closing');
        content.addEventListener(
          'animationend',
          () => {
            content.classList.remove('closing');
            parent.classList.remove('active');
            content.style.visibility = 'hidden';
          },
          { once: true }
        );
      } else {
        content.style.setProperty('--faq-height', content.scrollHeight + 'px');

        parent.classList.add('active');
        content.style.visibility = 'visible';
      }
    }
  }

customElements.define('custom-faq-richtext-section', CustomFaqRichtextSection);
  
// custom banner video image card section js 

class CustomBannerCardSection extends HTMLElement {
    constructor() {
        super();
        this.init();
    }

    init() {
        this.stickySection = this.querySelector(".banner-bottom-animated-data-wrapper");
        this.stickyContentWrapper = this.querySelector(".bottom-sticky-content");
        this.stickyContent = this.querySelector(".scale-heading");
        this.leftSideWrapper = this.querySelector('.bottom-left-side-data');
        this.rightSideWrapper = this.querySelector('.bottom-right-side-data');

        if (!this.stickySection || !this.stickyContentWrapper) return;

        this.images = [
            ...this.leftSideWrapper.children,
            ...this.rightSideWrapper.children
        ];

        this.prevScrollY = window.scrollY;
        this.updateScrollEffects();
      window.addEventListener("scroll", () => this.updateScrollEffects());
      this.mobileImagesAlignment();
    }
     
    mobileImagesAlignment() {  
      if (window.innerWidth <= 749) {
        const addOrRemoveClass = (selector, addClass, removeClass) => {  
        const lastChild = this.querySelector(`${selector}:last-child`);  
        const firstChild = this.querySelector(`${selector}:first-child`);  

        if (lastChild) lastChild.classList.add(addClass);  
        if (firstChild) firstChild.classList.remove(removeClass);  
        };  

        addOrRemoveClass('.bottom-left-side-data .data-wrapper', 'left', 'left');  
        addOrRemoveClass('.bottom-right-side-data .data-wrapper', 'left', 'left');  
      }
    } 

    updateScrollEffects() {

        const stickySectionRecttext = this.stickySection.getBoundingClientRect();
        const stickyContentRect = this.stickyContentWrapper.getBoundingClientRect();
        let viewportHeight = window.innerHeight;
        let stickyContentHeight = this.stickyContentWrapper.offsetHeight;
        let isSticky = stickySectionRecttext.top <= 0;

        if (!isSticky) {
            return;
        }

        let blankHeight = stickyContentHeight;
        let visibleHeight = Math.min(blankHeight, viewportHeight - Math.max(0, stickyContentRect.top));
        let scrollProgress = Math.min(1, Math.max(0, visibleHeight / blankHeight));
        let newscaleProgress = scrollProgress + 0.5;
        let scaleFactor = 1 - (scrollProgress * 0.9);
        let opacityFactor = 1 - (scrollProgress * 1);

        this.stickyContent.style.transform = `scale(${Math.max(0.3, scaleFactor)})`;
        // this.stickyContent.style.opacity = Math.max(0.3, opacityFactor);
      
        if (window.scrollY < this.prevScrollY) {
            scrollProgress = 1 - scrollProgress;
            newscaleProgress = 1 - newscaleProgress;
        } else {
          
        }

      // images scroll 
        const stickySectionRect = this.stickySection.getBoundingClientRect();
        let centerPoint = stickySectionRect.top + (stickySectionRect.height / 1);
        this.images.forEach(image => {
            const imageRect = image.getBoundingClientRect();
            const imageCenter = imageRect.top + (imageRect.height / 1);
            
            if (imageCenter < centerPoint) {
                image.style.transform = `rotate(0deg)`;
            } else {
                if (image.classList.contains("left")) {
                    image.style.transform = `rotate(15deg)`;
                } else {
                    image.style.transform = `rotate(-15deg)`;
                }
            }
        });
    }
}

customElements.define("custom-banner-card-section", CustomBannerCardSection);

// custom selling plan js
class CustomSellingPlan extends HTMLElement {
    constructor() {
        super();
    }

    connectedCallback() {
        this.init();
        this.checkSellingPlanOnLoad();
        this.handleSelectionChange();
        const sellingPLanFirstSelectedLoad = document.querySelector('input#subscription-plan-default');
        if (sellingPLanFirstSelectedLoad) {
          sellingPLanFirstSelectedLoad.checked = true;
          const event = new Event('change', { bubbles: true });
          sellingPLanFirstSelectedLoad.dispatchEvent(event);
        }
        this.querySelectorAll('.subscription-plan-group-wrapper-main input[name="selling_plan"]').forEach(input => {
            input.addEventListener('change', () => this.sellingPlanChecker(input));
        });

        this.querySelectorAll('.sellingplan-group-frequency-wrapper input[name="selling_plan_new"]').forEach(input => {
            input.addEventListener('change', () => {
                this.frequencyData(input);
                this.handleSelectionChange();
                const activeSellingPlanChecked = document.querySelector('.subscription-widgets-item-upper input[name="selling_plan"]');
                if (activeSellingPlanChecked) {
                    activeSellingPlanChecked.checked = true;
                    this.sellingPlanChecker(activeSellingPlanChecked);
                }
            });
        });
    }

    init() {
        document.addEventListener("change", (event) => {
            if (event.target.closest("fieldset.product-form__input")) {
                setTimeout(() => this.handleSelectionChange(), 50);
                setTimeout(() => this.removeWidgetsAppstel(), 1000);
                setTimeout(() => this.removeWidgetsAppstel(), 2000);
                setTimeout(() => this.removeWidgetsAppstel(), 500);
            }
        });
    }

    async removeWidgetsAppstel() {
        try {
            let productPageSellingPlanWidget = document.querySelector('section.product-page-section.custom-featured-section .appstle_sub_widget');
            if (!productPageSellingPlanWidget) {
                await new Promise((resolve) => setTimeout(resolve, 100));
                productPageSellingPlanWidget = document.querySelector('section.product-page-section.custom-featured-section .appstle_sub_widget');
            }
            if (productPageSellingPlanWidget) {
                productPageSellingPlanWidget.remove();
            }
        } catch (error) {
            console.error('Error removing widget:', error);
        }
    }

    frequencyData(input) {
        if (input.checked) {
            const checkedFrequencyData = input.value;
            const planActiveData = document.querySelector('input[name="selling_plan"]:checked');
            if (planActiveData && planActiveData.value === 'active') {
                document.querySelectorAll('.selling-plan-input-ap').forEach(item => item.remove());
                const sellingPlanInput = `<input checked="" value="${checkedFrequencyData}" name="selling_plan" class="selling-plan-input-ap" id="appstle_selling_plan_label_20" type="hidden">`;
                const productForm = document.querySelector('.custom-featured-section product-form form');
                if (productForm) {
                    productForm.insertAdjacentHTML('beforeend', sellingPlanInput);
                }
            }
        }
    }

    checkSellingPlanOnLoad() {
        const activeSellingPlan = document.querySelector('.subscription-plan-group-wrapper-main input[name="selling_plan"]:checked');
        if (activeSellingPlan) {
            this.sellingPlanChecker(activeSellingPlan);
        }
        this.handleSelectionChange(); // Ensure proper selection on load
    }

    sellingPlanChecker(input) {
        const checkedSellingPlan = input.value;
        if (checkedSellingPlan === 'active') {
            const loadingSelectedPlan = document.querySelector('.sellingplan-group-frequency-wrapper input[name="selling_plan_new"]:checked');
            document.querySelectorAll('.selling-plan-input-ap').forEach(item => item.remove());
            const planFrequencyGroupname = document.querySelector('.sticky-plan-frequecy');
            const planActiveFrequencyPrice = document.querySelector('.price-alocation-sticky');
            const planNormalPrice = document.querySelector('.price-orignal-sticky');
            if (planActiveFrequencyPrice && planNormalPrice) {
              planActiveFrequencyPrice.classList.remove('hide');
              planNormalPrice.classList.add('hide');
            }
            if (planFrequencyGroupname) {
              const groupNameChecked = input.getAttribute('data-group-name');
              planFrequencyGroupname.innerHTML = groupNameChecked;
            }
            if (loadingSelectedPlan) {
                const sellingPlanInput = `<input checked="" value="${loadingSelectedPlan.value}" name="selling_plan" class="selling-plan-input-ap" id="appstle_selling_plan_label_20" type="hidden">`;
                const productForm = document.querySelector('.custom-featured-section product-form form');
                if (productForm) {
                    productForm.insertAdjacentHTML('beforeend', sellingPlanInput);
                }
            }
        } else {
          document.querySelectorAll('.selling-plan-input-ap').forEach(item => item.remove());
          const planActiveFrequencyPrice = document.querySelector('.price-alocation-sticky');
            const planNormalPrice = document.querySelector('.price-orignal-sticky');
            if (planActiveFrequencyPrice && planNormalPrice) {
              planActiveFrequencyPrice.classList.add('hide');
              planNormalPrice.classList.remove('hide');
            }
            const planFrequencyGroupname = document.querySelector('.sticky-plan-frequecy');
            if (planFrequencyGroupname) {
              const groupNameChecked = input.getAttribute('data-group-name');
              planFrequencyGroupname.innerHTML = groupNameChecked;
            }
        }
        this.handleSelectionChange();
    }

    handleSelectionChange() {
        const selectedValues = [
            this.getCheckedValue("fieldset.custom-1"),
            this.getCheckedValue("fieldset.custom-2"),
            this.getCheckedValue("fieldset.custom-3")
        ].filter(value => value);

        if (selectedValues.length === 0) return;
        const selectedPair = selectedValues.join(" / ");
        const selectedCheckedPlan = document.querySelector('.sellingplan-group-frequency-wrapper input[name="selling_plan_new"]:checked');
        document.querySelectorAll(".sticky-add-to-cart-header .sticky-variant-title").forEach(el => el.innerHTML = selectedPair + ' Cans');
        const matchingOption = [...document.querySelectorAll(".hidden-select-price-allocation select option")].find(option => 
            option.getAttribute("data-title") === selectedPair && 
            option.getAttribute("data-allocation-id") === (selectedCheckedPlan ? selectedCheckedPlan.value : "")
        );
        if (matchingOption) {
          this.updatePrice(".sale-bage-selling-plan", matchingOption.getAttribute('data-discount'));
          let price = parseFloat(matchingOption.getAttribute("price-alocation").replace(/[^0-9.]/g, ''));
          let updatedPrice = (Math.ceil(price) - 0.01).toFixed(2);
      }
      
    }

    getCheckedValue(fieldsetSelector) {
        const fieldset = document.querySelector(fieldsetSelector);
        if (!fieldset) return null;
        const checkedInput = fieldset.querySelector("input:checked");
        return checkedInput ? checkedInput.value.trim() : null;
    }

    updatePrice(selector, value) {
        document.querySelectorAll(selector).forEach(el => el.textContent = value);
    }
}

window.addEventListener("DOMContentLoaded", () => {
    if (!customElements.get("custom-selling-plan")) {
        customElements.define("custom-selling-plan", CustomSellingPlan);
    }
});

// custom testimonial slider js 
class CustomSliderNewText extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('.testimonial-slider_slider_wrapper');
    this.items = this.querySelectorAll('.testimonial_block_itmes');
    this.slidesToShow =  2;
    this.columnsMobile = 1;
    this.columnsTablet = 1;
    this.totalItems = this.items.length - this.slidesToShow + 1;
    this.slidesToScroll = 1;
    this.isAutoplay =  'false';
    this.isLoop =  'true';
    this.isDraggable = 'true';
    this.hasArrows =  'true';
    this.hasDots = 'false';

    this.isDragging = false;
    this.startX = 0;
    this.dragDistance = 0;
    this.locked = false;
    this.threshold = 50;
    this.currentIndex = 0;

    this.init();
  }

  init() {
    this.setResponsive();
    this.createControls();
    this.setAutoplay();
    this.updateSlider();
    if (this.isDraggable) {
      this.addDraggableFeature();
    }
    this.addWheelFeature();
  }

  setResponsive() {
    const handleResize = () => {
      if (window.innerWidth <= 768) {
        this.slidesToShow = this.columnsMobile;
      } else if (window.innerWidth <= 992) {
        this.slidesToShow = this.columnsTablet;
      } else {
        this.slidesToShow = 2;
      }
      this.totalItems = this.items.length - this.slidesToShow + 1;
      this.updateSlider();
    };

    window.addEventListener('resize', handleResize);
    handleResize();
  }

  updateSlider() {
    const slideWidth = 100 / this.slidesToShow;
    const offset = -(this.currentIndex * slideWidth);
    this.slider.style.transform = `translateX(${offset}%)`;
    this.slider.style.userSelect = 'none';
    this.slider.setAttribute('draggable', 'false');
    this.slider.style.transition = 'all .3s linear';

    this.items.forEach((slide) => {
      slide.style.width = `${slideWidth}%`;
      slide.style.userSelect = 'none';
      slide.setAttribute('draggable', 'false');
    });

    if (this.dots) {
      this.dots.forEach((dot, index) => {
        dot.classList.toggle('active', index === this.currentIndex);
      });
    }
  }

  createControls() {
    if (this.slidesToShow < this.items.length) {
      const sliderContainer = this.querySelector('.slider-container');
      
      if (sliderContainer) {
        if (this.hasArrows) this.createArrows(sliderContainer);
        if (this.hasDots) this.createDots(sliderContainer);
      }
    }
  }

  createArrows(container) {
    
    const nextArrow = document.createElement('button');
    nextArrow.classList.add('next-arrow');
    nextArrow.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 256" focusable="false" color="currentColor" style="user-select: none; width: 100%; height: 100%; display: inline-block; fill: currentColor; color:currentColor; flex-shrink: 0;"><g color="currentColor" weight="regular"><path d="M181.66,133.66l-80,80a8,8,0,0,1-11.32-11.32L164.69,128,90.34,53.66a8,8,0,0,1,11.32-11.32l80,80A8,8,0,0,1,181.66,133.66Z"></path></g></svg>`;
    nextArrow.addEventListener('click', () => {
      this.moveSlide('next');
      this.stopAutoplayForDelay();
    });

    // Append arrows to the container
    // container.appendChild(prevArrow);
    container.appendChild(nextArrow);
  }

  createDots(container) {
    const dotsContainer = document.createElement('div');
    dotsContainer.classList.add('dots');

    this.items.forEach((_, index) => {
      const dot = document.createElement('button');
      dot.classList.add('dot');
      dot.addEventListener('click', () => {
        this.goToSlide(index);
        this.stopAutoplayForDelay();
      });
      dotsContainer.appendChild(dot);
    });

    container.appendChild(dotsContainer);
    this.dots = dotsContainer.querySelectorAll('.dot');
  }

  moveSlide(direction) {
    if (this.isLoop) {
      this.currentIndex = direction === 'next'
        ? (this.currentIndex + 1) % this.totalItems
        : (this.currentIndex - 1 + this.totalItems) % this.totalItems;
    } else {
      this.currentIndex = direction === 'next'
        ? Math.min(this.currentIndex + 1, this.totalItems - 1)
        : Math.max(this.currentIndex - 1, 0);
    }
    this.updateSlider();
  }

  goToSlide(index) {
    this.currentIndex = index;
    this.updateSlider();
  }

  setAutoplay() {
    
  }

  stopAutoplayForDelay() {
    
  }

  addDraggableFeature() {
    this.slider.addEventListener('mousedown', (e) => this.startDrag(e));
    this.slider.addEventListener('mousemove', (e) => this.moveDrag(e));
    this.slider.addEventListener('mouseup', () => this.endDrag());
    this.slider.addEventListener('mouseleave', () => this.endDrag());
    this.slider.addEventListener('touchstart', (e) => this.startDrag(e));
    this.slider.addEventListener('touchmove', (e) => this.moveDrag(e));
    this.slider.addEventListener('touchend', () => this.endDrag());
  }

  startDrag(e) {
    this.isDragging = true;
    this.startX = e.pageX || e.touches[0].pageX;
    this.dragDistance = 0;
    this.slider.style.cursor = 'grabbing';
    this.locked = false;
  }
  
  moveDrag(e) {
    if (!this.isDragging) return;
  
    const currentX = e.pageX || e.touches[0].pageX;
    this.dragDistance += currentX - this.startX;
  
    if (Math.abs(this.dragDistance) >= this.threshold) {
      if (!this.locked) {
        this.locked = true;
        this.moveSlide(this.dragDistance < 0 ? 'next' : 'prev');
      }
    }
  
    this.startX = currentX;
  }
  
  endDrag() {
    this.isDragging = false;
    this.slider.style.cursor = 'grab';
  
    setTimeout(() => {
      this.locked = false;
    }, 300);
  }
  addWheelFeature() {
      let hasScrolled = false;
  
      this.slider.addEventListener('wheel', (e) => {
          if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
              return;
          }
  
          e.preventDefault();
  
          if (hasScrolled) return;
  
          hasScrolled = true; 
  
          this.isLoop = false;
  
          if (e.deltaY > 0 || e.deltaX > 0) {
              this.moveSlide('next');
          } else {
              this.moveSlide('prev');
          }
          this.stopAutoplayForDelay();
  
          setTimeout(() => {
              hasScrolled = false;
              this.isLoop = true;
          }, 200); 
      }, { passive: false });
  }

}
 
customElements.define('testimonial-slider', CustomSliderNewText);


window.onload = function () {
  const sellingPLanFirstSelectedLoad = document.querySelector('input#subscription-plan-default');
  if (sellingPLanFirstSelectedLoad) {
    sellingPLanFirstSelectedLoad.checked = true;
    const event = new Event('change', { bubbles: true });
    sellingPLanFirstSelectedLoad.dispatchEvent(event);
  }
  const checkedInput = document.querySelector('variant-selects .custom_quantity_cans.product-form__input input:checked');

  if (checkedInput) {

    // Check if the checked input has the class 'triggerSelect'
    if (checkedInput.classList.contains('triggerSelect')) {
      const variantId = checkedInput.getAttribute('data-variant-id');

      // If data-variant-id is not empty, store it in the variable
      if (variantId) {
        const currentCheckedVal = variantId;
        
        // Find the <select> element and replace its value with currentCheckedVal
        const selectElement = document.querySelector('.product-form .product-variant-id[name="id"]');
        if (selectElement) {
          selectElement.value = currentCheckedVal;
          
        }
      }
    }
  } else {
    console.log("No input is checked.");
  }
  

    async function removeWidget() {
      try {
          let productPageSellingPlanWidget = document.querySelector('section.product-page-section.custom-featured-section .appstle_sub_widget');

          if (!productPageSellingPlanWidget) {
              await new Promise((resolve) => setTimeout(resolve, 100));
              productPageSellingPlanWidget = document.querySelector('section.product-page-section.custom-featured-section .appstle_sub_widget');
          }

          if (productPageSellingPlanWidget) {
              productPageSellingPlanWidget.remove();
            
          }
      } catch (error) {
          console.error('Error removing widget:', error);
      }
    }

    removeWidget();

    const stickyAddButton = document.querySelector('.button_add-cart.sticky-add-button .button');
    if (stickyAddButton) {
      stickyAddButton.addEventListener('click', function () {
        const productPageAddBtn = document.querySelector('.product-form__buttons .add-button-product');
        if (productPageAddBtn) {
          productPageAddBtn.click();
        }
      })
    }
};

// multi faq blocks js 
class CustomMultiFaq extends HTMLElement {
    constructor() {
        super();
    }

    connectedCallback() {
        this.init();
        this.checkActiveTabOnLoad();
    }

    init() {
        this.faqTabs = this.querySelectorAll('.custom-faq-tabs-wrapper .tabs-faq-block');
        this.faqBlocks = this.querySelectorAll('.custom-richtext-faq-data .custom-faq-blocks');

        this.addClickEvents();
    }

    addClickEvents() {
        this.faqTabs.forEach(tab => {
            tab.addEventListener('click', (event) => this.handleTabClick(event, tab));
        });
    }

    handleTabClick(event, selectedTab) {
        this.faqTabs.forEach(tab => tab.classList.remove('active'));
        selectedTab.classList.add('active');

        const selectedType = selectedTab.getAttribute('data-type');

        if (selectedType === "All") {
            this.showAllFaqBlocks();
        } else {
            this.filterFaqBlocks(selectedType);
        }
    }

    showAllFaqBlocks() {
        this.faqBlocks.forEach(block => {
            block.style.display = 'block';
        });
    }

    filterFaqBlocks(selectedType) {
        this.faqBlocks.forEach(block => {
            const blockTypes = block.getAttribute('data-type').split(',').map(type => type.trim());

            if (blockTypes.includes(selectedType)) {
                block.style.display = 'block';
            } else {
                block.style.display = 'none';
            }
        });
    }

    checkActiveTabOnLoad() {
        let activeTab = this.querySelector('.custom-faq-tabs-wrapper .tabs-faq-block.active');
        
        if (!activeTab) {
            activeTab = this.faqTabs[0];
            activeTab.classList.add('active');
        }

        const selectedType = activeTab.getAttribute('data-type');

        if (selectedType === "All") {
            this.showAllFaqBlocks();
        } else {
            this.filterFaqBlocks(selectedType);
        }
    }
}

customElements.define('custom-multi-faq-section', CustomMultiFaq);

//------------- custom js for Quick buy product start-------//

document.addEventListener('click', (e) => {
  if (e.target.matches('.itg_bottom_buy')) {
    const product_id = e.target.getAttribute('data-id');
    const product_url = e.target.getAttribute('data-url');
    const AddToCart = e.target.getAttribute('data-addToCart');
    if (product_id && AddToCart ===  "true") {
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: product_id, quantity: 1 }),
      })
        .then(() => {
          document.querySelector('cart-drawer') ? updateCartDrawer() : (window.location.href = '/cart');
        })
        .catch(console.error);
    }else{
       if (product_url) window.location.href = product_url;
    }
  }
});
function updateCartDrawer() {
  fetch(`${routes.cart_url}?section_id=cart-drawer`)
    .then((response) => response.text())
    .then((responseText) => {
      const html = new DOMParser().parseFromString(responseText, 'text/html');
      const selectors = ['#CartDrawer'];
      for (const selector of selectors) {
        const targetElement = document.querySelector(selector);
        const sourceElement = html.querySelector(selector);
        if (targetElement && sourceElement) {
          targetElement.replaceWith(sourceElement);
        }
      }
    })
    .then(()=>{
        document.querySelector('cart-drawer')?.classList.add('active');
        document.querySelector('#CartDrawer-Checkout')?.removeAttribute('disabled');
        document.querySelector('cart-drawer')?.classList.remove('is-empty');
        const drawer_empty = document.querySelector('.drawer__inner-empty');
        if (drawer_empty) {
          drawer_empty.style.display = 'none';
        }
    })
    .catch((e) => {
      console.error(e);
    });
}
//------------- custom js for Quick buy product End-------//


//------------- custom js for all sequence faid type of animations (as per class of "fade-sequence" class ) start -------//

document.addEventListener('DOMContentLoaded', function () {

  const fadeElems = document.querySelectorAll('.fade-sequence');
  if (fadeElems) {
    fadeElems.forEach((item) => {
      if (!item.querySelector('span')) {
        const words = item.textContent.trim().split(' ');
        item.innerHTML = words.map((word) => `<span>${word}</span>`).join(' ');
      } else {
        [...item.children].forEach((span) => {
          if (span.textContent.includes(' ')) {
            const splitWords = span.textContent.split(' ');
            span.innerHTML = splitWords.map((word) => `<span >${word}</span>`).join(' ');
          }
        });
      }
      setTimeout(() => {
      }, 200);
      item.querySelectorAll('span').forEach((span, i) => {
        span.style.transitionDelay = `${i * 100}ms`;
      });
    });
  }
});

// document.addEventListener("DOMContentLoaded", function () {
//   document.querySelectorAll("img").forEach(img => {
//       let loadingAttr = img.getAttribute("loading");
//       if (!loadingAttr || loadingAttr !== "lazy") {
//           img.setAttribute("loading", "lazy");
//       }
//   });
// });

// document.addEventListener("DOMContentLoaded", function () {
//   document.querySelectorAll(".animated-main-bottle-img-wrapper img").forEach(img => {
//       let loadingAttr = img.getAttribute("loading");
//       if (!loadingAttr || loadingAttr !== "eager") {
//           img.setAttribute("loading", "eager");
//       }
//   });
// });

  // document.addEventListener("DOMContentLoaded", function () {
  //   const section = document.querySelector('.animated-banner-video-wrapper');
  //   if (!section) return;
  //   const video = section.querySelector('video');
  //   const sources = video?.querySelectorAll('source');

  //   if (!video || !sources.length) return;

  //   const loadAndPlayVideo = () => {
  //     sources.forEach(source => {
  //       const dataSrc = source.getAttribute('data-src');
  //       if (dataSrc) {
  //         source.setAttribute('src', dataSrc);
  //       }
  //     });

  //     video.load();
  //     video.play().catch(() => {
  //       // Fallback if autoplay fails (e.g., not muted or mobile)
  //       console.warn("Autoplay failed or blocked by browser");
  //     });
  //   };

  //   const observer = new IntersectionObserver((entries, obs) => {
  //     entries.forEach(entry => {
  //       if (entry.isIntersecting) {
  //         loadAndPlayVideo();
  //         obs.unobserve(section);
  //       }
  //     });
  //   }, {
  //     root: null,
  //     threshold: 0.3 // Adjust this to control how close it needs to be
  //   });

  //   observer.observe(section);
  // });


  document.addEventListener("DOMContentLoaded", function () {
  // Reusable function to load and play a video
  const loadAndPlayVideo = (video) => {
    const sources = video?.querySelectorAll('source');
    if (!video || !sources.length) return;

    sources.forEach(source => {
      const dataSrc = source.getAttribute('data-src');
      if (dataSrc && !source.getAttribute('src')) {
        source.setAttribute('src', dataSrc);
      }
    });

    video.load();
    video.play().catch(() => {
      console.warn("Autoplay failed or blocked by browser");
    });
  };

  // Intersection Observer setup
  const observer = new IntersectionObserver((entries, obs) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        const video = entry.target;
        loadAndPlayVideo(video);
        obs.unobserve(video); // Stop observing after play starts
      }
    });
  }, {
    root: null,
    threshold: 0.3
  });

  // Case 1: Single banner video
  const singleSection = document.querySelector('.animated-banner-video-wrapper video');
  if (singleSection) observer.observe(singleSection);

  // Case 2: Multiple videos in bottom section
  const multiSectionVideos = document.querySelectorAll('.bottom-sticky-main video');
  multiSectionVideos.forEach(video => observer.observe(video));
});



//------------- custom js for all sequence faid type of animations (as per class of "fade-sequence" class ) End -------//



// custom multi faq slider js

class CustomMultiFaqStickySection extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.initStickyFaq();
    }

    initStickyFaq() {
      this.querySelectorAll('.faq-heading').forEach((item) => {
        item.addEventListener('click', () => this.toggleStickyFaq(item));
      });
    }

    toggleStickyFaq(item) {
      let parent = item.closest('.custom-faq-blocks');
      let content = parent.querySelector('.faq-content-custom');

      if (!content) return;

      if (parent.classList.contains('active')) {
        content.style.setProperty('--faq-height', content.scrollHeight + 'px');

        content.classList.add('closing');
        content.addEventListener(
          'animationend',
          () => {
            content.classList.remove('closing');
            parent.classList.remove('active');
            content.style.visibility = 'hidden';
          },
          { once: true }
        );
      } else {
        content.style.setProperty('--faq-height', content.scrollHeight + 'px');

        parent.classList.add('active');
        content.style.visibility = 'visible';
      }
    }
  }

customElements.define('custom-multi-faq-sticky', CustomMultiFaqStickySection);



// custom faq sticky js


// document.addEventListener("DOMContentLoaded", function () {
//   const wrapper = document.querySelector(".faq_main_multi-list_wrapper");
//   const sidebar = document.querySelector(".faq_sticky_sidebar");

//   console.log(wrapper , sidebar ) ;
  
//   if (!wrapper || !sidebar) return; // Exit if either is missing
  
//   console.log(wrapper , sidebar ) ;

//   // Clear sidebar first (in case it has content)
//   sidebar.innerHTML = "";

//   // Find all h3s with data-heading inside wrapper
//   const headings = wrapper.querySelectorAll("h3[data-title]");

//   console.log(headings ) ;

//   headings.forEach((heading, index) => {

//     // console.log(heading) ;

//     const text = heading.getAttribute("data-title") || heading.textContent.trim();

//     // Create li
//     const li = document.createElement("li");

//     // Optional: make them clickable and scroll to the section
//     const link = document.createElement("a");
//     link.textContent = text;
//     link.href = `#faq-heading-${index}`; 

//     // Add ID to the h3 for anchor targeting
//     heading.setAttribute("id", `faq-heading-${index}`);

//     li.appendChild(link);
//     sidebar.appendChild(li);
//   });

// });


document.addEventListener("DOMContentLoaded", function () {
  const wrapper = document.querySelector(".faq_main_multi-list_wrapper");
  const sidebar = document.querySelector(".faq_sticky_sidebar");
  const mobileDropdown = document.querySelector(".faq_mobile_dropdown"); // Add this to your HTML

  if (!wrapper || !sidebar) return;

  // Clear sidebar
  sidebar.innerHTML = "";
  if (mobileDropdown) mobileDropdown.innerHTML = ""; // Clear dropdown if exists

  const headings = wrapper.querySelectorAll("h3[data-title]");

  headings.forEach((heading, index) => {
    const text = heading.getAttribute("data-title") || heading.textContent.trim();
    const headingId = `faq-heading-${index}`;
    
    // Assign unique ID to each heading
    heading.setAttribute("id", headingId);

    // === Desktop Sidebar (li + a) ===
    const li = document.createElement("li");
    const link = document.createElement("a");
    link.textContent = text;
    link.href = `#${headingId}`;
    li.appendChild(link);
    sidebar.appendChild(li);

    // === Mobile Dropdown (option) ===
    if (mobileDropdown) {
      const option = document.createElement("option");
      option.value = headingId;
      option.textContent = text;
      mobileDropdown.appendChild(option);
    }
  });

  // Handle dropdown change → scroll to heading
  if (mobileDropdown) {
    mobileDropdown.addEventListener("change", function () {
      const selectedId = this.value;
      const target = document.getElementById(selectedId);
      if (target) {
        target.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    });
  }
});


document.addEventListener("DOMContentLoaded", function () {
  const searchInput = document.getElementById("Faq-sidebar-search");
  const wrapper = document.querySelector(".faq_main_multi-list_wrapper");
  const noResultsMessage = document.querySelector(".no-results-message");

  if (!searchInput || !wrapper || !noResultsMessage) return;


  
    searchInput.addEventListener("input", function () {
      const query = this.value.trim().toLowerCase();
      const queryWords = query.split(/\s+/).filter(Boolean); // split into words

      const contentBlocks = wrapper.querySelectorAll(".faq-content-custom");
      const faq_headings = wrapper.querySelectorAll(".faq-heading");

      if (query === "") {
        // Reset all
        wrapper.querySelectorAll(".custom-faq-blocks").forEach(block => {
          block.classList.remove("active", "hidden_blocks");
        });
        wrapper.querySelectorAll(".multi_faq_sticky_wrapper").forEach(section => {
          section.classList.remove("hidden_wrapper");
        });
        noResultsMessage.style.display = "none";
        return;
      }

      // Hide all initially
      wrapper.querySelectorAll(".custom-faq-blocks").forEach(block => {
        block.classList.add("hidden_blocks");
        block.classList.remove("active");
      });

      // Function to match words in a text
      const matchesQuery = (text) => {
        const lowerText = text.toLowerCase();
        return queryWords.every(word => lowerText.includes(word));
      };

      // Match in content
      contentBlocks.forEach(block => {
        const parentBlock = block.closest(".custom-faq-blocks");
        if (matchesQuery(block.textContent)) {
          parentBlock?.classList.add("active");
          parentBlock?.classList.remove("hidden_blocks");
        }
      });

      // Match in headings
      faq_headings.forEach(headText => {
        const parentBlock = headText.closest(".custom-faq-blocks");
        if (matchesQuery(headText.textContent)) {
          parentBlock?.classList.add("active");
          parentBlock?.classList.remove("hidden_blocks");
        }
      });

      // Hide wrappers with no visible blocks
      let allHidden = true;
      wrapper.querySelectorAll(".multi_faq_sticky_wrapper").forEach(section => {
        const visibleBlocks = section.querySelectorAll(".custom-faq-blocks:not(.hidden_blocks)");
        if (visibleBlocks.length === 0) {
          section.classList.add("hidden_wrapper");
        } else {
          section.classList.remove("hidden_wrapper");
          allHidden = false;
        }
      });

      // Show/hide "no results" message
      noResultsMessage.style.display = allHidden ? "block" : "none";
    });








});



document.addEventListener('DOMContentLoaded', function () {
const reviewLink = document.querySelector('.jdgm-write-rev-link');
  if (reviewLink) {
    document.querySelector('.jdgm-write-rev-link').addEventListener('click', function (e) {
      e.preventDefault();
      document.querySelector('.jdgm-form-wrapper').style.display = 'block';
    });
  }
});




// video grid lazy loading js

document.addEventListener("DOMContentLoaded", function () {
  const lazyVideos = document.querySelectorAll("video.lazy-video");

  if ("IntersectionObserver" in window) {
    const videoObserver = new IntersectionObserver((entries, observer) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const video = entry.target;

          const sources = video.querySelectorAll("source[data-src]");
          sources.forEach(source => {
            source.src = source.dataset.src;
            source.removeAttribute("data-src");
          });

          video.load(); 
          // video.play();  

          observer.unobserve(video); 
        }
      });
    }, { threshold: 0.5 }); 

    lazyVideos.forEach(video => {
      videoObserver.observe(video);
    });
  }
});


document.addEventListener("DOMContentLoaded", function () {
  const lazyImages = document.querySelectorAll("img.lazy-marquee");

  if ("IntersectionObserver" in window) {
    const imageObserver = new IntersectionObserver((entries, observer) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const img = entry.target;

          if (img.dataset.src) {
            img.src = img.dataset.src;
            img.removeAttribute("data-src");
          }
          if (img.dataset.srcset) {
            img.srcset = img.dataset.srcset;
            img.removeAttribute("data-srcset");
          }

          observer.unobserve(img); // stop observing once loaded
        }
      });
    }, { rootMargin: "200px", threshold: 0.1 }); 
    // rootMargin = preload a bit earlier

    lazyImages.forEach(img => {
      imageObserver.observe(img);
    });
  }
});


// Savings section js

document.addEventListener("DOMContentLoaded", function () {

  var countdownDate = new Date("2025-12-02T00:00:00").getTime();

  function pad(num) {
    return num < 10 ? "0" + num : num;
  }

  // Cache DOM elements safely
  const daysEl = document.getElementById("days");
  const hoursEl = document.getElementById("hours");
  const minutesEl = document.getElementById("minutes");
  const secondsEl = document.getElementById("seconds");
  const timerEl = document.getElementById("countdown-timer");

  var countdownFunction = setInterval(function () {
    var now = new Date().getTime();
    var distance = countdownDate - now;

    var days = Math.floor(distance / (1000 * 60 * 60 * 24));
    var hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    var minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
    var seconds = Math.floor((distance % (1000 * 60)) / 1000);

    // Update ONLY if elements exist
    if (daysEl) daysEl.innerHTML = pad(days);
    if (hoursEl) hoursEl.innerHTML = pad(hours);
    if (minutesEl) minutesEl.innerHTML = pad(minutes);
    if (secondsEl) secondsEl.innerHTML = pad(seconds);

    if (distance < 0) {
      clearInterval(countdownFunction);
      if (timerEl) timerEl.innerHTML = "EXPIRED";
    }
  }, 1000);
});

// Savings section js


// sticky header js


document.addEventListener("DOMContentLoaded", function () {
    const stickyHeader = document.getElementById("sticky_header_group_section");
    const showAt = 300; // px from top to show sticky header

    window.addEventListener("scroll", function () {
      if(stickyHeader){
        if (window.scrollY > showAt) {
            stickyHeader.classList.add("is-visible");
          } else {
            stickyHeader.classList.remove("is-visible");
        }
      }
    });

  

  // FIRST cart icon (original header)
  const firstCart = document.querySelector("header .header__icon--cart");

  // SECOND cart icon (duplicate)
  const secondCart = document.querySelectorAll(".header__icon--cart")[1]; 
  // second index = duplicate icon

  if (firstCart && secondCart) {

    secondCart.addEventListener("click", function (event) {
      // duplicate link ko rokna
      event.preventDefault();

      // first/cart icon mein click trigger karna
      firstCart.click();
    });

  }


});


// sticky header js